import React, { useState, useEffect } from 'react';
import { X, Calendar, Moon, Users, School, Trophy, FileText, CheckCircle } from 'lucide-react';
import axios from 'axios';

var API = process.env.REACT_APP_BACKEND_URL || '';

export default function DailyTracker({ isOpen, onClose, calendarData }) {
  var [step, setStep] = useState(1);
  var [responses, setResponses] = useState({
    date: new Date().toISOString().split('T')[0],
    overnight_scheduled_for: null,  // From calendar
    overnight_actually_with: null,  // User's answer
    overnight_withheld: null,
    withheld_by: null,
    exchange_completed: null,
    school_responsible: null,
    school_attendance: null,
    events_attended: [],
    journal_entry: '',
  });
  var [saving, setSaving] = useState(false);

  // Parse calendar data for today
  var hasOvernight = calendarData?.overnights?.length > 0;
  var scheduledOvernightPerson = hasOvernight ? calendarData.overnights[0]?.person : null;
  var hasExchange = calendarData?.exchanges?.length > 0;
  var hasSchool = calendarData?.schoolDay || false;
  var events = calendarData?.events || [];

  var totalSteps = 1 + (hasOvernight ? 1 : 0) + (hasExchange ? 1 : 0) + (hasSchool ? 1 : 0) + (events.length > 0 ? 1 : 0) + 1;

  if (!isOpen) return null;

  function updateResponse(key, value) {
    setResponses(function(prev) { return { ...prev, [key]: value }; });
  }

  function handleNext() {
    // Validate current step before proceeding
    var currentStepNum = 1;
    
    // Check overnight step
    if (hasOvernight) {
      if (step === currentStepNum) {
        // Must answer who had the overnight
        if (responses.overnight_actually_with === undefined) {
          return;
        }
        // If different from scheduled or neither, must answer withheld question
        if (responses.overnight_actually_with !== scheduledOvernightPerson) {
          if (responses.overnight_withheld === null) {
            return;
          }
          // If withheld, must specify who
          if (responses.overnight_withheld === true && !responses.withheld_by) {
            return;
          }
        }
      }
      currentStepNum++;
    }
    
    // Check exchange step
    if (hasExchange) {
      if (step === currentStepNum && responses.exchange_completed === null) {
        return;
      }
      currentStepNum++;
    }
    
    // Check school step
    if (hasSchool) {
      if (step === currentStepNum) {
        // Must have attendance status
        if (!responses.school_attendance) {
          return;
        }
        // If not "not_in_school", must have responsible party
        if (responses.school_attendance !== 'not_in_school' && !responses.school_responsible) {
          return;
        }
      }
      currentStepNum++;
    }
    
    // Events step doesn't require validation (can skip all events)
    
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  }

  function isCurrentStepValid() {
    var currentStepNum = 1;
    
    // Check overnight step
    if (hasOvernight) {
      if (step === currentStepNum) {
        if (responses.overnight_actually_with === undefined) {
          return false;
        }
        if (responses.overnight_actually_with !== scheduledOvernightPerson) {
          if (responses.overnight_withheld === null) {
            return false;
          }
          if (responses.overnight_withheld === true && !responses.withheld_by) {
            return false;
          }
        }
        return true;
      }
      currentStepNum++;
    }
    
    // Check exchange step
    if (hasExchange) {
      if (step === currentStepNum) {
        return responses.exchange_completed !== null;
      }
      currentStepNum++;
    }
    
    // Check school step
    if (hasSchool) {
      if (step === currentStepNum) {
        // Must have attendance status
        if (!responses.school_attendance) {
          return false;
        }
        // If not "not_in_school", must have responsible party
        if (responses.school_attendance !== 'not_in_school' && !responses.school_responsible) {
          return false;
        }
        return true;
      }
      currentStepNum++;
    }
    
    // Events and journal steps are always valid (optional)
    return true;
  }

  function handleSubmit() {
    setSaving(true);
    axios.post(API + '/api/daily-tracker', responses, { withCredentials: true })
      .then(function() {
        onClose();
      })
      .catch(function(err) {
        console.error('Failed to save daily tracker:', err);
        alert('Failed to save. Please try again.');
      })
      .finally(function() {
        setSaving(false);
      });
  }

  function renderStep() {
    var currentStep = 1;
    
    // Step: Overnight
    if (hasOvernight) {
      if (step === currentStep) {
        // Set scheduled person from calendar when component loads
        if (!responses.overnight_scheduled_for && scheduledOvernightPerson) {
          updateResponse('overnight_scheduled_for', scheduledOvernightPerson);
        }
        
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#EEEFF8] border border-[#DDE1EE] flex items-center justify-center">
                <Moon className="w-6 h-6 text-[#5A4E9E]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#2D2560]">Overnight Tracking</h3>
                <p className="text-sm text-[#8C86C4]">
                  Calendar shows: {scheduledOvernightPerson === 'me' ? 'Your' : "Co-parent's"} scheduled overnight
                </p>
              </div>
            </div>
            
            {/* Question 1: Who actually had the overnight? */}
            <div className="space-y-3">
              <p className="text-[#2D2560] font-medium mb-3">
                Who actually had the child overnight?
              </p>
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={function() { 
                    updateResponse('overnight_actually_with', 'me');
                  }}
                  className={'p-3 rounded-lg border-2 transition-all ' + (responses.overnight_actually_with === 'me' ? 'border-[#5A4E9E] bg-[#5A4E9E]/10' : 'border-[#DDE1EE] hover:border-[#A8AAD4]')}
                >
                  <p className="text-sm font-medium text-[#2D2560]">Me</p>
                </button>
                <button
                  onClick={function() { 
                    updateResponse('overnight_actually_with', 'coparent');
                  }}
                  className={'p-3 rounded-lg border-2 transition-all ' + (responses.overnight_actually_with === 'coparent' ? 'border-[#5A4E9E] bg-[#5A4E9E]/10' : 'border-[#DDE1EE] hover:border-[#A8AAD4]')}
                >
                  <p className="text-sm font-medium text-[#2D2560]">Co-Parent</p>
                </button>
                <button
                  onClick={function() { 
                    updateResponse('overnight_actually_with', null);
                  }}
                  className={'p-3 rounded-lg border-2 transition-all ' + (responses.overnight_actually_with === null && step > 0 ? 'border-[#5A4E9E] bg-[#5A4E9E]/10' : 'border-[#DDE1EE] hover:border-[#A8AAD4]')}
                >
                  <p className="text-sm font-medium text-[#2D2560]">Neither</p>
                </button>
              </div>
            </div>

            {/* Question 2: Was overnight withheld? (only if actual !== scheduled or neither) */}
            {responses.overnight_actually_with !== undefined && 
             (responses.overnight_actually_with !== scheduledOvernightPerson) && (
              <div className="space-y-3 pt-2 border-t border-[#DDE1EE]">
                <p className="text-[#2D2560] font-medium mb-3 mt-3">
                  Was this overnight prevented or withheld by one parent?
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={function() { updateResponse('overnight_withheld', true); }}
                    className={'p-3 rounded-lg border-2 transition-all ' + (responses.overnight_withheld === true ? 'border-[#EF4444] bg-[#EF4444]/10' : 'border-[#DDE1EE] hover:border-[#A8AAD4]')}
                  >
                    <p className="text-sm font-medium text-[#2D2560]">Yes</p>
                  </button>
                  <button
                    onClick={function() { 
                      updateResponse('overnight_withheld', false);
                      updateResponse('withheld_by', null);
                    }}
                    className={'p-3 rounded-lg border-2 transition-all ' + (responses.overnight_withheld === false ? 'border-[#10B981] bg-[#10B981]/10' : 'border-[#DDE1EE] hover:border-[#A8AAD4]')}
                  >
                    <p className="text-sm font-medium text-[#2D2560]">No</p>
                  </button>
                </div>
              </div>
            )}

            {/* Question 3: Who withheld? (only if withheld === true) */}
            {responses.overnight_withheld === true && (
              <div className="space-y-3 pt-2">
                <p className="text-[#2D2560] font-medium mb-3">
                  Who prevented the overnight?
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={function() { updateResponse('withheld_by', 'me'); }}
                    className={'p-3 rounded-lg border-2 transition-all ' + (responses.withheld_by === 'me' ? 'border-[#5A4E9E] bg-[#5A4E9E]/10' : 'border-[#DDE1EE] hover:border-[#A8AAD4]')}
                  >
                    <p className="text-sm font-medium text-[#2D2560]">I Did</p>
                  </button>
                  <button
                    onClick={function() { updateResponse('withheld_by', 'coparent'); }}
                    className={'p-3 rounded-lg border-2 transition-all ' + (responses.withheld_by === 'coparent' ? 'border-[#5A4E9E] bg-[#5A4E9E]/10' : 'border-[#DDE1EE] hover:border-[#A8AAD4]')}
                  >
                    <p className="text-sm font-medium text-[#2D2560]">Co-Parent</p>
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      }
      currentStep++;
    }

    // Step: Exchange
    if (hasExchange) {
      if (step === currentStep) {
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#EEEFF8] border border-[#DDE1EE] flex items-center justify-center">
                <Users className="w-6 h-6 text-[#5A4E9E]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#2D2560]">Exchange Day</h3>
                <p className="text-sm text-[#8C86C4]">Scheduled child exchange today</p>
              </div>
            </div>
            <p className="text-[#2D2560] mb-4">Was the exchange completed successfully?</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={function() { updateResponse('exchange_completed', true); }}
                className={'p-4 rounded-xl border-2 transition-all ' + (responses.exchange_completed === true ? 'border-[#10B981] bg-[#10B981]/10' : 'border-[#DDE1EE] hover:border-[#A8AAD4]')}
              >
                <CheckCircle className={'w-6 h-6 mx-auto mb-2 ' + (responses.exchange_completed === true ? 'text-[#10B981]' : 'text-[#8C86C4]')} />
                <p className="text-sm font-medium text-[#2D2560]">Yes</p>
              </button>
              <button
                onClick={function() { updateResponse('exchange_completed', false); }}
                className={'p-4 rounded-xl border-2 transition-all ' + (responses.exchange_completed === false ? 'border-[#EF4444] bg-[#EF4444]/10' : 'border-[#DDE1EE] hover:border-[#A8AAD4]')}
              >
                <X className={'w-6 h-6 mx-auto mb-2 ' + (responses.exchange_completed === false ? 'text-[#EF4444]' : 'text-[#8C86C4]')} />
                <p className="text-sm font-medium text-[#2D2560]">No</p>
              </button>
            </div>
          </div>
        );
      }
      currentStep++;
    }

    // Step: School
    if (hasSchool) {
      if (step === currentStep) {
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#EEEFF8] border border-[#DDE1EE] flex items-center justify-center">
                <School className="w-6 h-6 text-[#5A4E9E]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#2D2560]">School Day</h3>
                <p className="text-sm text-[#8C86C4]">Track school attendance</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-[#2D2560] mb-3">Who was responsible for getting child to school?</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={function() { updateResponse('school_responsible', 'me'); }}
                    className={'p-3 rounded-lg border-2 transition-all ' + (responses.school_responsible === 'me' ? 'border-[#5A4E9E] bg-[#5A4E9E]/10' : 'border-[#DDE1EE] hover:border-[#A8AAD4]')}
                  >
                    <p className="text-sm font-medium text-[#2D2560]">Me</p>
                  </button>
                  <button
                    onClick={function() { updateResponse('school_responsible', 'coparent'); }}
                    className={'p-3 rounded-lg border-2 transition-all ' + (responses.school_responsible === 'coparent' ? 'border-[#5A4E9E] bg-[#5A4E9E]/10' : 'border-[#DDE1EE] hover:border-[#A8AAD4]')}
                  >
                    <p className="text-sm font-medium text-[#2D2560]">Co-Parent</p>
                  </button>
                </div>
              </div>
              <div>
                <p className="text-[#2D2560] mb-3">Attendance status:</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={function() { updateResponse('school_attendance', 'present'); }}
                    className={'p-3 rounded-lg border-2 transition-all ' + (responses.school_attendance === 'present' ? 'border-[#10B981] bg-[#10B981]/10' : 'border-[#DDE1EE] hover:border-[#A8AAD4]')}
                  >
                    <p className="text-sm font-medium text-[#2D2560]">On Time</p>
                  </button>
                  <button
                    onClick={function() { updateResponse('school_attendance', 'late'); }}
                    className={'p-3 rounded-lg border-2 transition-all ' + (responses.school_attendance === 'late' ? 'border-[#EAB308] bg-[#EAB308]/10' : 'border-[#DDE1EE] hover:border-[#A8AAD4]')}
                  >
                    <p className="text-sm font-medium text-[#2D2560]">Late</p>
                  </button>
                  <button
                    onClick={function() { updateResponse('school_attendance', 'absent'); }}
                    className={'p-3 rounded-lg border-2 transition-all ' + (responses.school_attendance === 'absent' ? 'border-[#EF4444] bg-[#EF4444]/10' : 'border-[#DDE1EE] hover:border-[#A8AAD4]')}
                  >
                    <p className="text-sm font-medium text-[#2D2560]">Absent</p>
                  </button>
                  <button
                    onClick={function() { updateResponse('school_attendance', 'sick'); }}
                    className={'p-3 rounded-lg border-2 transition-all ' + (responses.school_attendance === 'sick' ? 'border-[#F97316] bg-[#F97316]/10' : 'border-[#DDE1EE] hover:border-[#A8AAD4]')}
                  >
                    <p className="text-sm font-medium text-[#2D2560]">Sick Day</p>
                  </button>
                  <button
                    onClick={function() { updateResponse('school_attendance', 'not_in_school'); updateResponse('school_responsible', null); }}
                    className={'p-3 rounded-lg border-2 transition-all col-span-2 ' + (responses.school_attendance === 'not_in_school' ? 'border-[#8C86C4] bg-[#8C86C4]/10' : 'border-[#DDE1EE] hover:border-[#A8AAD4]')}
                  >
                    <p className="text-sm font-medium text-[#2D2560]">Not in School (Too Young / Summer Break)</p>
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      }
      currentStep++;
    }

    // Step: Events
    if (events.length > 0) {
      if (step === currentStep) {
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#EEEFF8] border border-[#DDE1EE] flex items-center justify-center">
                <Trophy className="w-6 h-6 text-[#5A4E9E]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#2D2560]">Child&apos;s Events</h3>
                <p className="text-sm text-[#8C86C4]">{events.length} event{events.length > 1 ? 's' : ''} scheduled today</p>
              </div>
            </div>
            <p className="text-[#2D2560] mb-3">Which events did you attend?</p>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {events.map(function(event, idx) {
                var isAttended = responses.events_attended.includes(event.id);
                return (
                  <button
                    key={idx}
                    onClick={function() {
                      updateResponse('events_attended', 
                        isAttended 
                          ? responses.events_attended.filter(function(id) { return id !== event.id; })
                          : [...responses.events_attended, event.id]
                      );
                    }}
                    className={'p-3 rounded-lg border-2 transition-all text-left ' + (isAttended ? 'border-[#10B981] bg-[#10B981]/10' : 'border-[#DDE1EE] hover:border-[#A8AAD4]')}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-[#2D2560]">{event.title}</p>
                      {isAttended && <CheckCircle className="w-5 h-5 text-[#10B981]" />}
                    </div>
                    {event.time && <p className="text-xs text-[#8C86C4] mt-1">{event.time}</p>}
                  </button>
                );
              })}
            </div>
          </div>
        );
      }
      currentStep++;
    }

    // Step: Journal Entry (always last)
    if (step === currentStep) {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-[#EEEFF8] border border-[#DDE1EE] flex items-center justify-center">
              <FileText className="w-6 h-6 text-[#5A4E9E]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#2D2560]">Daily Journal Entry</h3>
              <p className="text-sm text-[#8C86C4]">Optional notes about today</p>
            </div>
          </div>
          <textarea
            value={responses.journal_entry}
            onChange={function(e) { updateResponse('journal_entry', e.target.value); }}
            placeholder="How was your day? Any notable moments with your child? (Optional)"
            className="w-full h-32 px-4 py-3 bg-[#EEEFF8] border-2 border-[#DDE1EE] rounded-xl text-[#2D2560] placeholder:text-[#8C86C4] focus:outline-none focus:border-[#5A4E9E] resize-none"
          />
        </div>
      );
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(45, 37, 96, 0.7)' }}>
      <div className="bg-white w-full max-w-lg rounded-2xl p-6 max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#EEEFF8] border border-[#DDE1EE] flex items-center justify-center">
              <Calendar className="w-5 h-5 text-[#5A4E9E]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#2D2560]">Daily Tracker</h2>
              <p className="text-xs text-[#8C86C4]">Step {step} of {totalSteps}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#EEEFF8] rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-[#8C86C4]" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="mb-6 h-2 bg-[#EEEFF8] rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#5A4E9E] to-[#3B82F6] transition-all duration-300"
            style={{ width: ((step / totalSteps) * 100) + '%' }}
          />
        </div>

        {/* Step Content */}
        <div className="mb-6">
          {renderStep()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex gap-3">
          {step > 1 && (
            <button
              onClick={function() { setStep(step - 1); }}
              className="flex-1 py-3 px-4 rounded-xl border-2 border-[#DDE1EE] hover:bg-[#EEEFF8] transition-colors font-medium text-[#2D2560]"
            >
              Back
            </button>
          )}
          <button
            onClick={handleNext}
            disabled={saving || !isCurrentStepValid()}
            className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-[#5A4E9E] to-[#3B82F6] hover:from-[#4A3E8E] hover:to-[#2B72E6] transition-all font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : step === totalSteps ? 'Complete' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
